import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@env/environment';
import { UserLoginResponse, UsersFormBody } from '@core/models/auth.models';
import {
  Fish,
  FishLength,
  FishLimit,
  FishLimitMoreNote,
  FishType,
  FishesFishType,
  FishingTechnique,
  MoreNote,
  NoaaStation,
  State,
} from '@core/models/reference-data.models';

/**
 * HTTP context token used by the auth interceptor to decide whether a
 * request's Authorization header should use the admin Basic-auth creds
 * (true) vs. the logged-in user's api_id (false, the default).
 */
export const USE_ADMIN_AUTH = new HttpContextToken<boolean>(() => false);

/** Envelope wrapper shape used by every reference-data GET endpoint. */
type Envelope<T> = Record<string, T[]>;

export interface ConfigsResponse {
  configs: Array<{
    id: number;
    data_version: number | string;
    schema_version: number | string;
    notes_font_size: string;
  }>;
}

/**
 * Thin wrapper around HttpClient for SaltwaterUp's REST surface.
 *
 * All GET methods return already-unwrapped arrays; the envelope
 * unwrapping happens inline via `map`. If an endpoint returns an
 * unexpected shape the `??` fallback returns an empty array so
 * downstream code doesn't blow up.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);

  // ------------------------------------------------------------------
  // Auth (POST /users.json with admin creds)
  // ------------------------------------------------------------------

  postUsers(body: UsersFormBody): Observable<UserLoginResponse> {
    const url = `${environment.restUrlBase}users.json`;
    const form = this.toFormUrlEncoded(
      body as unknown as Record<string, string | undefined>,
    );

    return this.http.post<UserLoginResponse>(url, form, {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
      context: new HttpContext().set(USE_ADMIN_AUTH, true),
    });
  }

  // ------------------------------------------------------------------
  // Reference-data GETs (authenticated with the logged-in user's api_id)
  // ------------------------------------------------------------------

  getConfigs(): Observable<ConfigsResponse['configs']> {
    const url = `${environment.restUrlBase}configs.json`;
    return this.http
      .get<ConfigsResponse>(url)
      .pipe(map((res) => res?.configs ?? []));
  }

  getStates(): Observable<State[]> {
    return this.getEnvelope<State>('states.json', 'states');
  }

  getFishTypes(): Observable<FishType[]> {
    return this.getEnvelope<FishType>('fishTypes.json', 'fishTypes');
  }

  getFishes(): Observable<Fish[]> {
    return this.getEnvelope<Fish>('fish.json', 'fish');
  }

  getFishesFishTypes(): Observable<FishesFishType[]> {
    return this.getEnvelope<FishesFishType>(
      'fishesFishTypes.json',
      'fishesFishTypes',
    );
  }

  getFishingTechniques(): Observable<FishingTechnique[]> {
    return this.getEnvelope<FishingTechnique>(
      'fishingTechniques.json',
      'fishingTechniques',
    );
  }

  getFishLengths(): Observable<FishLength[]> {
    return this.getEnvelope<FishLength>('fishLengths.json', 'fishLengths');
  }

  getFishLimits(): Observable<FishLimit[]> {
    return this.getEnvelope<FishLimit>('fishLimits.json', 'fishLimits');
  }

  getFishLimitsMoreNotes(): Observable<FishLimitMoreNote[]> {
    return this.getEnvelope<FishLimitMoreNote>(
      'fishLimitsMoreNotes.json',
      'fishLimitsMoreNotes',
    );
  }

  getMoreNotes(): Observable<MoreNote[]> {
    return this.getEnvelope<MoreNote>('moreNotes.json', 'moreNotes');
  }

  getNoaaStations(): Observable<NoaaStation[]> {
    return this.getEnvelope<NoaaStation>('noaaStations.json', 'noaaStations');
  }

  // ------------------------------------------------------------------
  // Catches (POST/GET/DELETE /fishStats.json)
  // ------------------------------------------------------------------

  /**
   * Creates or updates a fish stat on the server. Form-encoded body
   * follows the iOS client's `getFormParamsForUpdate` format exactly
   * so the server's PHP handler doesn't need any changes.
   *
   * The server returns a `fishStats` array with a single hydrated
   * FishStat payload — the catch plus all server-derived NOAA/tide/
   * moon/sun/weather fields. Caller should merge this back into the
   * local record.
   */
  postFishStat(
    body: Record<string, string | undefined>,
  ): Observable<FishStatResponse> {
    const url = `${environment.restUrlBase}fishStats.json`;
    const form = this.toFormUrlEncoded(body);
    return this.http.post<FishStatResponse>(url, form, {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    });
  }

  /**
   * Fetches the logged-in user's fish stats for a given month/year.
   * Server returns them ordered by date_and_time desc.
   */
  getFishStatsForMonth(
    userId: number,
    deviceId: string,
    month: number,
    year: number,
  ): Observable<FishStatResponse> {
    const url =
      `${environment.restUrlBase}fishStats.json` +
      `?user_id=${userId}&device_id=${encodeURIComponent(deviceId)}` +
      `&local_id=0&action=get_by_month_and_year` +
      `&month=${month}&year=${year}`;
    return this.http.get<FishStatResponse>(url);
  }

  /**
   * Marks a fish stat as deleted on the server. Local delete happens
   * separately and doesn't wait for network.
   */
  deleteFishStat(
    userId: number,
    deviceId: string,
    localId: number,
    remoteId: number,
  ): Observable<FishStatResponse> {
    const url =
      `${environment.restUrlBase}fishStats.json` +
      `?user_id=${userId}&device_id=${encodeURIComponent(deviceId)}` +
      `&local_id=${localId}&action=delete_by_id&remote_id=${remoteId}`;
    return this.http.get<FishStatResponse>(url);
  }

  /**
   * Returns the count of catches the server has on file for this user.
   * Used as the first step of a post-login download to know how many
   * iterations of get_next_id/get_by_id we'll need.
   *
   * Port of SaltwaterUpRestClient.m getNumFishStatsRemote (line 325).
   * Server response shape: { num_fish_stats: <number-as-string> }.
   */
  getNumFishStats(
    userId: number,
    deviceId: string,
  ): Observable<NumFishStatsResponse> {
    // local_id is required by the iOS URL builder even though it's
    // unused for this action — the endpoint just inspects user_id.
    // We send 0 to satisfy the parser.
    const url =
      `${environment.restUrlBase}fishStats.json` +
      `?user_id=${userId}&device_id=${encodeURIComponent(deviceId)}` +
      `&local_id=0&action=get_num_fish_stats`;
    return this.http.get<NumFishStatsResponse>(url);
  }

  /**
   * Walks the user's catches in id order. Pass the previous fishStat
   * id (or 0 to start) and the server returns the next one's id.
   * iOS pattern from SaltwaterUpRestClient.m getNextFishStatIdWithLastId
   * (line 363). Returns 0/null/empty when there are no more.
   *
   * Server response shape: { fishStats: [{ id: <next> }] } — same
   * fishStats-array-with-one-element shape as POST. We only need the
   * id field, but the response is a full record (with placeholder
   * data for fields the server doesn't fill on this action).
   */
  getNextFishStatId(
    userId: number,
    deviceId: string,
    lastRemoteId: number,
  ): Observable<FishStatResponse> {
    const url =
      `${environment.restUrlBase}fishStats.json` +
      `?user_id=${userId}&device_id=${encodeURIComponent(deviceId)}` +
      `&local_id=0&remote_id=${lastRemoteId}&action=get_next_id`;
    return this.http.get<FishStatResponse>(url);
  }

  /**
   * Fetches a single catch by remote id, including its photo as
   * base64 image_data if one is attached. iOS pattern from
   * getFishStatWithId (line 401).
   */
  getFishStatById(
    userId: number,
    deviceId: string,
    remoteId: number,
  ): Observable<FishStatResponse> {
    const url =
      `${environment.restUrlBase}fishStats.json` +
      `?user_id=${userId}&device_id=${encodeURIComponent(deviceId)}` +
      `&local_id=0&remote_id=${remoteId}&action=get_by_id`;
    return this.http.get<FishStatResponse>(url);
  }

  /**
   * Asks the server for catches recorded under similar conditions
   * (location, time of day/year, NOAA station readings) to the given
   * point. Used on the catch detail page to surface "you've caught
   * fish in conditions like this before" hints.
   *
   * Response shape (port of FishingCondition.m line 145):
   *   { conditions: [ { fish_stat_id, similar_reason }, ... ] }
   *
   * `fish_stat_id` here is the *remote* id (the server's primary key),
   * which we map back to local FishStat records via the remote_id index.
   */
  getSimilarConditions(
    userId: number,
    lat: number,
    lon: number,
    dateAndTime: string,
    timezone: string,
  ): Observable<SimilarConditionsResponse> {
    const url =
      `${environment.restUrlBase}conditions.json` +
      `?action=get_similar_stats&user_id=${userId}` +
      `&lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}` +
      `&date_and_time=${encodeURIComponent(dateAndTime)}` +
      `&timezone=${encodeURIComponent(timezone)}`;
    return this.http.get<SimilarConditionsResponse>(url);
  }

  /**
   * Fetches the environmental conditions (sun, moon, tide, water,
   * weather, hourly water-level series) for a given lat/lon at a
   * given time. Powers the Conditions tab and tide graph.
   *
   * Optional `noaaStationNumber` overrides the server's default
   * "nearest station" choice — used when the user manually picks a
   * station from the dropdown. Pass 0 (or omit) to let the server pick.
   *
   * Response shape:
   *   {
   *     conditions: [
   *       {
   *         moonrise, sunrise, ...all the scalar fields...,
   *         water_level_actuals: [{t, v}, ...],
   *         water_level_predictions: [{t, v}, ...]
   *       }
   *     ]
   *   }
   *
   * The conditions array always has length 1 for this action — same
   * pattern as fishStats POST responses.
   */
  getConditions(
    lat: number,
    lon: number,
    dateAndTime: string,
    timezone: string,
    noaaStationNumber?: number,
  ): Observable<ConditionsApiResponse> {
    let url =
      `${environment.restUrlBase}conditions.json` +
      `?action=get_conditions` +
      `&lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}` +
      `&date_and_time=${encodeURIComponent(dateAndTime)}` +
      `&timezone=${encodeURIComponent(timezone)}`;
    if (noaaStationNumber && noaaStationNumber > 0) {
      url += `&noaa_station_number=${noaaStationNumber}`;
    }
    return this.http.get<ConditionsApiResponse>(url);
  }

  // ------------------------------------------------------------------
  // Internals
  // ------------------------------------------------------------------

  private getEnvelope<T>(path: string, envelopeKey: string): Observable<T[]> {
    const url = `${environment.restUrlBase}${path}`;
    return this.http
      .get<Envelope<T>>(url)
      .pipe(map((res) => res?.[envelopeKey] ?? []));
  }

  private toFormUrlEncoded(obj: Record<string, string | undefined>): string {
    return Object.entries(obj)
      .filter((entry): entry is [string, string] => entry[1] !== undefined)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
  }
}

/**
 * Shape of the `fishStats.json` endpoint response.
 *
 * For POST (single record) and certain GET actions, `fishStats` IS the
 * record — the FishStat fields (id, fish_id, moonrise, ...) sit
 * directly on it alongside `status`. There's no records-array wrapper.
 *
 * For "get_by_month_and_year" and similar list endpoints, `fishStats`
 * is an array of records. Callers that consume those endpoints check
 * Array.isArray() to disambiguate.
 *
 * The PHP/MySQL backend serializes everything as strings, so consumers
 * normalize via the same toNum/toStr helpers used for reference data.
 */
export type FishStatResponse =
  | { fishStats: Record<string, unknown> | Record<string, unknown>[] | null }
  | null;

/**
 * Response shape for /apiv1/fishStats.json?action=get_num_fish_stats.
 *
 * The server wraps the count inside a `fishStats` envelope rather
 * than putting it at the top level — confirmed against the live API
 * after seeing this in dev console:
 *   { fishStats: { num_fish_stats: "69" } }
 *
 * Like everything else on this API, the count comes back as a string
 * ("12" not 12) and may be missing/null when the user has none.
 */
export interface NumFishStatsResponse {
  fishStats?: {
    num_fish_stats?: number | string | null;
  } | null;
}

/**
 * Shape of the `/apiv1/conditions.json?action=get_similar_stats`
 * endpoint. Each conditions[i] points to a previously-recorded catch
 * (by its server-side fish_stat_id, which is our local remote_id) and
 * provides a human-readable reason it's similar — e.g. "Same lat/lon
 * and tide phase," "Caught within 1 hour of sunset on a similar day
 * of year." The reason is rendered verbatim on the UI.
 */
export interface SimilarConditionsResponse {
  conditions?: Array<{
    fish_stat_id: number | string;
    similar_reason: string;
  }>;
}

/**
 * Shape of the `/apiv1/conditions.json?action=get_conditions` response.
 *
 * The conditions array always has exactly one element for this action.
 * That element has all the scalar conditions fields directly, plus two
 * nested arrays for hourly water-level data (predictions for the full
 * day, actuals for past hours only).
 *
 * Each water-level entry is a `{t, v}` pair where `t` is the timestamp
 * in MySQL datetime format and `v` is the level in feet. Some entries
 * have empty strings — actuals beyond the current time, or sparse
 * coverage from less-monitored stations. Consumers filter those out
 * (per WaterLevel.m line 33).
 */
export interface ConditionsApiResponse {
  conditions?: Array<
    Record<string, unknown> & {
      water_level_actuals?: Array<{ t: string; v: string }>;
      water_level_predictions?: Array<{ t: string; v: string }>;
    }
  >;
}

